import { useState, useEffect } from "react";

// FX conversion for portfolio totals. Rates come from the free, keyless
// frankfurter API (ECB reference rates, CORS-enabled) and are cached in
// localStorage for 12 hours. `fx` objects have the shape
// { base: "GBP", rates: { USD: 1.27, EUR: 1.17, GBP: 1, ... } }
// where rates are units of currency per 1 unit of base.

const FX_TTL_MS = 12 * 60 * 60 * 1000;
const BASE_CURRENCY_KEY = "baseCurrency";
const DEFAULT_BASE = "GBP";

export const SUPPORTED_BASES = ["GBP", "USD", "EUR", "CHF", "SEK", "DKK", "NOK", "PLN"];

export function getBaseCurrency() {
  try {
    const stored = window.localStorage.getItem(BASE_CURRENCY_KEY);
    if (stored && SUPPORTED_BASES.includes(stored)) return stored;
  } catch (_) { /* storage unavailable */ }
  return DEFAULT_BASE;
}

export function setBaseCurrency(currency) {
  try {
    window.localStorage.setItem(BASE_CURRENCY_KEY, currency);
  } catch (_) { /* storage unavailable */ }
}

// Converts a value from `from` currency into the fx base currency.
// Returns null when the value or the needed rate is unavailable.
export function convertToBase(value, from, fx) {
  if (value === null || value === undefined) return null;
  if (!fx || !fx.base || !from) return null;
  if (from === fx.base) return value;
  const rate = fx.rates?.[from];
  if (!rate) return null;
  return value / rate;
}

export async function fetchFxRates(base) {
  const cacheKey = `fxRates:${base}`;
  try {
    const cached = JSON.parse(window.localStorage.getItem(cacheKey));
    if (cached && Date.now() - cached.cachedAt < FX_TTL_MS) return cached.rates;
  } catch (_) { /* no/invalid cache */ }

  const res = await fetch(`https://api.frankfurter.dev/v1/latest?base=${encodeURIComponent(base)}`);
  if (!res.ok) throw new Error(`FX rate fetch failed: ${res.status}`);
  const data = await res.json();
  const rates = { ...data.rates, [base]: 1 };

  try {
    window.localStorage.setItem(cacheKey, JSON.stringify({ rates, cachedAt: Date.now() }));
  } catch (_) { /* storage unavailable */ }
  return rates;
}

// Loads FX rates for the given base currency. Returns { fx, error } where
// fx is null until rates arrive (callers fall back to per-currency display).
export function useFxRates(base) {
  const [fx, setFx] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setFx(null);
    setError(null);
    fetchFxRates(base)
      .then((rates) => { if (!cancelled) setFx({ base, rates }); })
      .catch((err) => {
        console.error("FX rates unavailable:", err);
        if (!cancelled) setError(err);
      });
    return () => { cancelled = true; };
  }, [base]);

  return { fx, error };
}
